import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DatabaseService } from './database.service';
import { filter, map, Observable, take, tap } from 'rxjs';
import { Expense } from '../models/expense';
import { RxDocument } from 'rxdb';
import { RxExpenseDocMethods } from '../RxDB';
import { AppConfigService } from '../app-config.service';

@Injectable({
  providedIn: 'root'
})
export class ExpenseService {

  constructor(
    private http: HttpClient,
    private databaseService: DatabaseService,
    private configService: AppConfigService
  ) {
    this.databaseService = databaseService;
    this.configService = configService;
    this.http.get(configService.getCsvFolderLocation() + "expenses.csv", { responseType: 'text' }).subscribe({
      next: (data: string) => {
        this.parseData(data);
      },
      error: () => {
      }
    });
  }

  private decodedURIValues = {
    "%2C":",",
    "%3A":":",
    "%26":"&",
    "%3B":";"
  }

  /**
   * Converts the CSV into data that can be inserted to the on-memory database
   * @param toConvert 
   */
  private parseData(toConvert: string) {
    let me = this;
    toConvert = toConvert.replaceAll("\r", "");

    let rows: Array<Array<string>> = toConvert.split("\n")
      .filter(line => line.length > 0).slice(1)
      .map(function (row) { return row.split(",") });

    let bulkArray:Expense[] = [];
    rows.forEach(function (row) {
      try{
        if(!(row.length === 1 && row[0] === "")){
          bulkArray.push({type:  me.decodeCsvField(row[0]),
            expense: me.decodeCsvField(row[1]),
            subcategory: me.decodeCsvField(row[2]),
            cost: Number(me.decodeCsvField(row[3]) ?? 0),
            eligibility: me.decodeCsvField(row[4]),
            information: me.decodeCsvField(row[5]),
            order: Number(me.decodeCsvField(row[6]) ?? 0),
            courseLoad: me.decodeCsvField(row[7])});
    
        }
      } catch (error) {
        console.error("Skipping malformed expenses.csv row", row, error);
      }
    });
    if(bulkArray.length >0){
      try{
        me.databaseService.db.expense.bulkUpsert(bulkArray as any[])
          .then(result => {
             result.error.forEach(err => console.error("Skipping malformed expenses.csv row", err));
           })
          .catch(error => console.error("Error bulk inserting expenses array: ", error));
      } catch (error) {
        console.error("Error bulk inserting expenses array: ", error);
      }
    }

  }

  /**
   * Returns a list of housing categories people would pick
   * @param selectedStudies (e.g. graduate or undergraduate)
   * @returns Observable Array<String> of housing types (e.g. Rent, etc.)
   */
  public getHousing(selectedStudies?: string) {
    return this.databaseService.db.expense.find({
      selector: {
        type: "Housing",
        $or: [{
          eligibility: "All"
        }, {
          eligibility: selectedStudies
        }]
      },
      sort: [
        { expense: 'desc' }
      ]
    }
    ).$.pipe(
      map(values => [... new Set(values.map(value => value.expense))])
    );
  }

  /**
   * Returns a list of housing options
   * @param selectedHousing What the user selected from getHousing
   * @param selectedStudies 
   * @returns Observable Array<Expense> that returns a list of housing options
   */
  public getSelectedHousingOptions(selectedHousing?: string, selectedStudies?: string) {
    return this.databaseService.db.expense.find({
      selector: {
        type: "Housing",
        expense: selectedHousing,
        $or: [{
          eligibility: "All"
        }, {
          eligibility: selectedStudies
        }]

      },
      sort: [
        { order: "asc" }
      ]
    }).$.pipe(
      take(1),
      map((results) => {
        let toReturn: Array<Expense> = [];
        results.forEach(result => {
          let newHousingOption: Expense = {
            type: result.type,
            expense: result.expense,
            eligibility: result.eligibility,
            cost: result.cost,
            subcategory: result.subcategory,
            information: result.information,
            order: result.order
          };
          toReturn.push(newHousingOption);
        })
        return toReturn;
      }),
    );
  }

  /**
   * Returns the housing option the user selected
   * @param selectedHousing 
   * @param selectedHousingOption 
   * @param selectedStudies 
   * @returns Observable Expense that the user selected
   */
  public getSelectedHousingOption(selectedHousing?: string, selectedHousingOption?: string, selectedStudies?: string) {
    return this.databaseService.db.expense.findOne({
      selector: {
        type: "Housing",
        expense: selectedHousing,
        subcategory: selectedHousingOption,
        $or: [{
          eligibility: "All"
        }, {
          eligibility: selectedStudies
        }]

      }
    }).$.pipe(
      take(1),
      map((result) => {
        let newHousingOption: Expense = {
          type: result?.type,
          expense: result?.expense,
          eligibility: result?.eligibility,
          cost: result?.cost,
          subcategory: result?.subcategory,
          information: result?.information,
          order: result?.order
        }
        return newHousingOption;
      }),
    );
  }

  /**
   * Gets all the potential meal plans available to a user
   * @param selectedStudies 
   * @returns Observable Array<String> containing the meals a user can select
   */
  public getMealPlans(selectedStudies?: string) {
    return this.databaseService.db.expense.find({
      selector: {
        type: "Meal Plan",
        $or: [{
          eligibility: "All"
        }, {
          eligibility: selectedStudies
        }]
      },
      sort: [
        { order: "asc" }
      ]
    }
    ).$.pipe(
      map(values => [... new Set(values.map(value => value.expense))])
    );
  }

  /**
   * Gets a meal plan information based on the user's choice
   * @param selectedMealPlan 
   * @param selectedStudies 
   * @returns Observable <Expense> that returns information about a meal plan
   */
  public getMealPlanOption(selectedMealPlan?: string, selectedStudies?: string) {
    return this.databaseService.db.expense.findOne({
      selector: {
        type: "Meal Plan",
        expense: selectedMealPlan,
        $or: [{
          eligibility: "All"
        }, {
          eligibility: selectedStudies
        }]
      }

    }).$.pipe(
      take(1),
      map((result) => {
        let mealPlan: Expense = {
          type: result?.type,
          expense: result?.expense,
          eligibility: result?.eligibility,
          cost: result?.cost,
          subcategory: result?.subcategory,
          information: result?.information,
          order: result?.order
        }
        return mealPlan;
      }),
    );
  }

  /**
   * Gets transportation options available to the user
   * @param selectedStudies 
   * @returns Observable Array<String> list of transportation options
   */
  public getTransportationOptions(selectedStudies?: string) {
    return this.databaseService.db.expense.find({
      selector: {
        type: "Transportation",
        $or: [{
          eligibility: "All"
        }, {
          eligibility: selectedStudies
        }]
      }
    }
    ).$.pipe(
      map(values => [... new Set(values.map(value => value.expense))])
    );
  }

  /**
   * Gets transportation option selected by the user
   * @param selectedTransportation 
   * @param selectedStudies 
   * @returns Observable <Expense> that shows information about the selected transportation
   */
  public getTransportationOption(selectedTransportation?: string, selectedStudies?: string) {
    return this.databaseService.db.expense.findOne({
      selector: {
        type: "Transportation",
        expense: selectedTransportation,
        $or: [{
          eligibility: "All"
        }, {
          eligibility: selectedStudies
        }]
      }
    }).$.pipe(
      take(1),
      map((result) => {
        let mealPlan: Expense = {
          type: result?.type,
          expense: result?.expense,
          eligibility: result?.eligibility,
          cost: result?.cost,
          subcategory: result?.subcategory,
          information: result?.information,
          order: result?.order
        }
        return mealPlan;
      }),
    );
  }

  /**
   * Retrieves all the fees a user has as either a graduate or undergraduate
   * @param selectedStudies 
   * @returns Observable Array<Expense> that contains all the student fees based on their studies
   */
  public getFees(selectedStudies?: string) {
    return this.databaseService.db.expense.find({
      selector: {
        type: "Fee",
        $or: [{
          eligibility: "All"
        }, {
          eligibility: selectedStudies
        }]
      },
      sort: [
        { order: "asc" }
      ]
    }).$.pipe(
      take(1),
      map((results) => {
        let fees: Array<Expense> = [];
        results.forEach(result => {
          let fee: Expense = {
            type: result?.type,
            expense: result?.expense,
            eligibility: result?.eligibility,
            cost: result?.cost,
            subcategory: result?.subcategory,
            information: this.decodeCsvField(result?.information ?? ""),
            order: result?.order
          }
          fees.push(fee);
        });

        return fees;
      }),
    );
  }

  /**
   * Retrieves all the fees a user has as either a graduate or undergraduate
   * @param selectedStudies 
   * @returns Observable Array<Expense> that contains all the student fees based on their studies
   */
  public getFeesCourseLoad(hasCourseLoad:boolean, selectedStudies?: string) {
    let partTimeCode:string = "PT";
    let fullTimeCode:string = "FT";
    const courseLoad:string = hasCourseLoad ? fullTimeCode : partTimeCode;
    return this.databaseService.db.expense.find({
      selector: {
        type: "Fee",
        $or: [{
          eligibility: "All"
        }, {
          eligibility: selectedStudies
        }],
        courseLoad:courseLoad
        
      },
      sort: [
        { order: "asc" }
      ]
    }).$.pipe(
      take(1),
      map((results) => {
        let fees: Array<Expense> = [];
        results.forEach(result => {
          let fee: Expense = {
            type: result?.type,
            expense: result?.expense,
            eligibility: result?.eligibility,
            cost: result?.cost,
            subcategory: result?.subcategory,
            information:  this.decodeCsvField(result?.information ?? ""),
            order: result?.order
          }
          fees.push(fee);
        });

        return fees;
      }),
    );
    

  }

  /**
   * Retrieves the cost of food in a year
   * @returns Observable <Expense> An estimated average cost of food per year
   */
  public getDefaultFoodCost():Observable<Expense> {
    return this.databaseService.db.expense.findOne({
      selector: {
        type: "Food"
      }

    }).$.pipe(
      filter(this.isNonNull),
      take(1),
      map((result) => {

        let foodCost: Expense = {
          type: result?.type,
          expense: result?.expense,
          eligibility: result?.eligibility,
          cost: result?.cost,
          subcategory: result?.subcategory,
          information: result?.information,
          order: result?.order
        }
        return foodCost;
      }),
    );
  }

  /**
   * Retrieves the cost of incidentals in a year
   * @returns Observable <Expense> An estimated average cost of incidentals per year 
   */
  public getDefaultIncidentalsCost() {
    return this.databaseService.db.expense.findOne({
      selector: {
        type: "Incidentals"
      }

    }).$.pipe(
      filter(this.isNonNull),
      take(1),
      map((result) => {
        let incidentalsCost: Expense = {
          type: result?.type,
          expense: result?.expense,
          eligibility: result?.eligibility,
          cost: result?.cost,
          subcategory: result?.subcategory,
          information: result?.information,
          order: result?.order
        }
        return incidentalsCost;
      }),
    );
  }

  private isNonNull<T>(value: T): value is NonNullable<T> {
    return value != null;
  }

  /**
   * Helper function that removes URI encoding in the CSV file. This was done to prevent weird things if user puts commas into information
   * @param raw the raw CSV file
   * @returns decoded string
   */
  private decodeCsvField(raw: string):string {
    return decodeURI(raw ?? "").replace(/%2C|%3A|%26|%3B/g, value => this.decodedURIValues[value as keyof typeof this.decodedURIValues]);
  }
}
